import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { cn } from '@/lib/utils';
import {
  createInitialEngine,
  POWER_SYNC_INTERVAL_MS,
  UI_SYNC_INTERVAL_MS,
} from './constants';
import {
  advanceEngine,
  buildUiState,
  cancelCharge,
  getSceneSnapshot,
  releaseCharge,
  startAttack,
  startCharge,
} from './engine';
import { getAimFromStageRatio } from './helpers';
import { createGameAudio } from './audio';
import HudOverlay, { PowerTray } from './HudOverlay';
import { getBoundedFrameDelta, resumeEngineClock } from './lifecycle';
import {
  createSceneRenderCache,
  drawScene,
  ensureCanvasContext,
  preloadSceneAssets,
  type SizedCanvas,
} from './render';
import { getRenderProfile, selectRenderQuality } from './quality';
import { RenderQuality, SoccerPenaltyChallengeProps, UiState } from './types';

const SoccerPenaltyChallenge = ({ className, variant = 'home' }: SoccerPenaltyChallengeProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef(createInitialEngine());
  const sceneCacheRef = useRef(createSceneRenderCache());
  const sizedCanvasRef = useRef<SizedCanvas | null>(null);
  const isVisibleRef = useRef(false);
  const requestResizeRef = useRef<() => void>(() => undefined);
  const requestDrawRef = useRef<() => void>(() => undefined);
  const uiLastPushedAtRef = useRef(0);
  const powerLastPushedAtRef = useRef(0);
  const audioRef = useRef(createGameAudio());
  const instructionsId = `penalty-run-stage-instructions-${variant}`;
  const statusTitleId = `penalty-run-status-title-${variant}`;
  const statusBodyId = `penalty-run-status-body-${variant}`;
  const initialUiState = buildUiState(createInitialEngine(), 0, variant);
  const [uiState, setUiState] = useState<UiState>(() => initialUiState);
  const [displayedPower, setDisplayedPower] = useState(initialUiState.power);
  const [visitBest, setVisitBest] = useState(0);
  const [muted, setMuted] = useState(false);
  const [quality, setQuality] = useState<RenderQuality>('full');
  const qualityRef = useRef<RenderQuality>('full');
  const displayedPowerRef = useRef(initialUiState.power);

  const pushUiState = useCallback(
    (nowMs: number) => {
      const nextUiState = buildUiState(engineRef.current, nowMs, variant);
      displayedPowerRef.current = nextUiState.power;
      setDisplayedPower(nextUiState.power);
      setUiState(nextUiState);
      if (nextUiState.phase === 'summary') {
        setVisitBest((currentBest) => Math.max(currentBest, nextUiState.runSummary.score));
      }
      uiLastPushedAtRef.current = nowMs;
      powerLastPushedAtRef.current = nowMs;
    },
    [variant],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const applyQuality = () => {
      const navigatorHints = navigator as Navigator & { deviceMemory?: number };
      const nextQuality = selectRenderQuality(
        {
          deviceMemory: navigatorHints.deviceMemory,
          hardwareConcurrency: navigator.hardwareConcurrency,
        },
        mediaQuery.matches,
      );
      qualityRef.current = nextQuality;
      setQuality(nextQuality);
      sceneCacheRef.current.invalidate();
      requestResizeRef.current();
    };

    applyQuality();
    mediaQuery.addEventListener?.('change', applyQuality);
    return () => mediaQuery.removeEventListener?.('change', applyQuality);
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!stage || !canvas) return;

    const engine = engineRef.current;
    let animationFrameId = 0;
    let running = false;
    let isIntersecting = false;
    let documentVisible = document.visibilityState !== 'hidden';
    let pausedAtMs: number | null = null;
    let lastRenderedAtMs = performance.now();
    let assetsRequested = false;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      sizedCanvasRef.current = ensureCanvasContext(
        canvas,
        getRenderProfile(qualityRef.current),
        rect.width,
        rect.height,
      );
      sceneCacheRef.current.invalidate();
      if (running && sizedCanvasRef.current) {
        drawScene(
          sizedCanvasRef.current,
          engine,
          performance.now(),
          getRenderProfile(qualityRef.current),
          sceneCacheRef.current,
        );
      }
    };

    const drawCurrentFrame = () => {
      if (!running || !sizedCanvasRef.current) return;
      drawScene(
        sizedCanvasRef.current,
        engine,
        performance.now(),
        getRenderProfile(qualityRef.current),
        sceneCacheRef.current,
      );
    };

    requestResizeRef.current = resizeCanvas;
    requestDrawRef.current = drawCurrentFrame;

    const tick = (nowMs: number) => {
      if (!running) return;

      const profile = getRenderProfile(qualityRef.current);
      const elapsedMs = nowMs - lastRenderedAtMs;
      if (elapsedMs >= profile.targetFrameMs - 2) {
        const previousPhase = engine.phase;
        advanceEngine(engine, nowMs, getBoundedFrameDelta(elapsedMs));

        if (previousPhase !== 'result' && engine.phase === 'result' && engine.shot) {
          audioRef.current.playOutcome(engine.shot.outcome, engine.shot.perfect);
        }

        if (sizedCanvasRef.current) {
          drawScene(
            sizedCanvasRef.current,
            engine,
            nowMs,
            profile,
            sceneCacheRef.current,
          );
        }

        const snapshot = getSceneSnapshot(engine, nowMs);
        if (
          nowMs - powerLastPushedAtRef.current >= POWER_SYNC_INTERVAL_MS &&
          Math.abs(snapshot.power - displayedPowerRef.current) > 0.0005
        ) {
          displayedPowerRef.current = snapshot.power;
          setDisplayedPower(snapshot.power);
          powerLastPushedAtRef.current = nowMs;
        }

        if (nowMs - uiLastPushedAtRef.current >= UI_SYNC_INTERVAL_MS) {
          pushUiState(nowMs);
        }
        lastRenderedAtMs = nowMs;
      }

      animationFrameId = window.requestAnimationFrame(tick);
    };

    const stopLoop = (nowMs: number) => {
      if (!running) return;
      running = false;
      isVisibleRef.current = false;
      pausedAtMs = nowMs;
      stage.dataset.animationRunning = 'false';
      window.cancelAnimationFrame(animationFrameId);
    };

    const startLoop = (nowMs: number) => {
      if (running || !isIntersecting || !documentVisible) return;

      resumeEngineClock(engine, pausedAtMs, nowMs);
      pausedAtMs = null;
      running = true;
      isVisibleRef.current = true;
      stage.dataset.animationRunning = 'true';

      if (engine.phase === 'idle') {
        startAttack(engine, nowMs);
      }

      resizeCanvas();
      pushUiState(nowMs);
      lastRenderedAtMs = nowMs - getRenderProfile(qualityRef.current).targetFrameMs;

      if (!assetsRequested) {
        assetsRequested = true;
        void preloadSceneAssets().then(() => {
          sceneCacheRef.current.invalidate();
          drawCurrentFrame();
        });
      }

      animationFrameId = window.requestAnimationFrame(tick);
    };

    const reconcileLoop = () => {
      const nowMs = performance.now();
      if (isIntersecting && documentVisible) startLoop(nowMs);
      else stopLoop(nowMs);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting = entry.isIntersecting && entry.intersectionRatio >= 0.05;
        reconcileLoop();
      },
      { threshold: [0, 0.05, 0.2, 0.6] },
    );
    observer.observe(stage);

    const handleVisibilityChange = () => {
      documentVisible = document.visibilityState !== 'hidden';
      reconcileLoop();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleResize = () => resizeCanvas();
    const resizeObserver =
      'ResizeObserver' in window
        ? new ResizeObserver(handleResize)
        : null;
    if (resizeObserver) {
      resizeObserver.observe(canvas);
    } else {
      window.addEventListener('resize', handleResize);
    }

    resizeCanvas();
    return () => {
      running = false;
      isVisibleRef.current = false;
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      requestResizeRef.current = () => undefined;
      requestDrawRef.current = () => undefined;
    };
  }, [pushUiState]);

  const syncAimFromClientX = (clientX: number) => {
    const stage = stageRef.current;
    if (!stage || engineRef.current.phase !== 'aim') return;

    const rect = stage.getBoundingClientRect();
    if (rect.width <= 0) return;

    const nextAim = getAimFromStageRatio((clientX - rect.left) / rect.width);
    if (Math.abs(nextAim - engineRef.current.aim) < 0.0005) return;

    engineRef.current.aim = nextAim;
    pushUiState(performance.now());
    requestDrawRef.current();
  };

  const beginCharge = (nowMs: number) => {
    if (!startCharge(engineRef.current, nowMs)) return false;
    pushUiState(nowMs);
    return true;
  };

  const cancelActiveCharge = (nowMs: number) => {
    if (!cancelCharge(engineRef.current)) return false;
    pushUiState(nowMs);
    return true;
  };

  const releaseActiveCharge = (nowMs: number) => {
    if (!releaseCharge(engineRef.current, nowMs)) return false;
    audioRef.current.playKick();
    pushUiState(nowMs);
    return true;
  };

  const reset = (event?: { stopPropagation?: () => void }) => {
    event?.stopPropagation?.();
    const nowMs = performance.now();
    Object.assign(engineRef.current, createInitialEngine());
    if (isVisibleRef.current) {
      startAttack(engineRef.current, nowMs);
    }
    sceneCacheRef.current.invalidate();
    pushUiState(nowMs);
    requestDrawRef.current();
  };

  const handleStagePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (engineRef.current.phase !== 'aim') return;
    if (
      event.pointerType === 'mouse' ||
      event.buttons !== 0 ||
      engineRef.current.chargeStartedAtMs !== null
    ) {
      syncAimFromClientX(event.clientX);
    }
  };

  const handleStagePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || engineRef.current.phase !== 'aim') return;

    event.preventDefault();
    event.currentTarget.focus();
    audioRef.current.activate();
    syncAimFromClientX(event.clientX);
    event.currentTarget.setPointerCapture(event.pointerId);
    beginCharge(performance.now());
  };

  const handleStagePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    releaseActiveCharge(performance.now());
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleStagePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    cancelActiveCharge(performance.now());
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleStageKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;

    if (engineRef.current.phase === 'summary' && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      reset();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      if (engineRef.current.phase !== 'aim') return;

      event.preventDefault();
      engineRef.current.aim = Math.max(
        0,
        Math.min(
          1,
          engineRef.current.aim + (event.key === 'ArrowLeft' ? -0.08 : 0.08),
        ),
      );
      pushUiState(performance.now());
      requestDrawRef.current();
      return;
    }

    if ((event.key !== ' ' && event.key !== 'Enter') || event.repeat) return;
    event.preventDefault();
    audioRef.current.activate();
    beginCharge(performance.now());
  };

  const handleStageKeyUp = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || (event.key !== ' ' && event.key !== 'Enter')) return;
    event.preventDefault();
    releaseActiveCharge(performance.now());
  };

  const toggleMuted = () => {
    const nextMuted = !muted;
    if (!nextMuted) audioRef.current.activate();
    audioRef.current.setMuted(nextMuted);
    setMuted(nextMuted);
  };

  return (
    <section
      data-testid="penalty-run"
      data-render-quality={quality}
      className={cn('relative overflow-hidden rounded-[2rem] [contain:layout_paint]', className)}
    >
      <h2 className="sr-only">Penalty Run soccer challenge</h2>
      <div
        ref={stageRef}
        data-testid="penalty-run-stage"
        data-animation-running="false"
        role="group"
        tabIndex={0}
        aria-label="Penalty Run soccer challenge"
        aria-describedby={`${instructionsId} ${statusTitleId} ${statusBodyId}`}
        className="group relative touch-pan-y select-none overflow-hidden rounded-[2rem] bg-[#050c16] text-left shadow-[0_30px_90px_rgba(2,6,23,0.28)] outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky-300/80 sm:cursor-crosshair"
        onPointerMove={handleStagePointerMove}
        onPointerDown={handleStagePointerDown}
        onPointerUp={handleStagePointerUp}
        onPointerCancel={handleStagePointerCancel}
        onLostPointerCapture={(event) => {
          if (engineRef.current.phase === 'aim') {
            cancelActiveCharge(performance.now());
          }
        }}
        onKeyDown={handleStageKeyDown}
        onKeyUp={handleStageKeyUp}
      >
        <span id={instructionsId} className="sr-only">
          Five-shot run. Move across the goal or use left and right arrows to aim. Hold on the pitch, Space, or Enter to charge. Release inside the marked perfect window to shoot. Vertical page scrolling remains available.
        </span>
        <span id={statusTitleId} data-testid="penalty-run-status-title" className="sr-only" aria-live="polite">
          {uiState.statusTitle}
        </span>
        <span id={statusBodyId} data-testid="penalty-run-status-body" className="sr-only">
          {uiState.statusBody}
        </span>
        <div
          className={cn(
            'relative w-full overflow-hidden',
            variant === 'home'
              ? 'aspect-[10/14.6] sm:aspect-[16/10.8] xl:aspect-[16/9.2]'
              : 'aspect-[10/14.2] sm:aspect-[16/10.4]',
          )}
        >
          <canvas
            ref={canvasRef}
            data-testid="penalty-run-canvas"
            className="h-full w-full"
            role="img"
            aria-label="Penalty Run scene with a striker, goalkeeper, visible aim target, power window, and distinct goal, save, or miss feedback"
          />
          <HudOverlay
            uiState={uiState}
            visitBest={visitBest}
            muted={muted}
            quality={quality}
            onReset={() => reset()}
            onReplay={() => reset()}
            onToggleMuted={toggleMuted}
          />
        </div>

        <PowerTray power={displayedPower} uiState={uiState} />
      </div>
    </section>
  );
};

export default SoccerPenaltyChallenge;
